import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

const ORANGE = '#F18A00';
const DARK = '#1A1A1A';
const POLL_MS = 5000;
const UNREAD_POLL_MS = 30000;

// ── Icônes SVG inline ─────────────────────────────────────────
const Icon = ({ d, size = 18, color = 'currentColor', children, stroke = 1.75 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {children || <path d={d} />}
  </svg>
);
const IconChat   = (c, s = 18) => <Icon size={s} color={c}><path d="M3 20l1.3 -3.9a9 8 0 1 1 3.4 2.9l-4.7 1"/><path d="M12 12l0 .01"/><path d="M8 12l0 .01"/><path d="M16 12l0 .01"/></Icon>;
const IconSend   = (c) => <Icon color={c}><path d="M10 14l11 -11"/><path d="M21 3l-6.5 18a.55 .55 0 0 1 -1 0l-3.5 -8l-8 -3.5a.55 .55 0 0 1 0 -1l18 -6.5"/></Icon>;
const IconClose  = (c) => <Icon color={c}><path d="M18 6l-12 12"/><path d="M6 6l12 12"/></Icon>;
const IconMin    = (c) => <Icon color={c}><path d="M5 12l14 0"/></Icon>;
const IconPlus   = (c) => <Icon color={c}><path d="M12 5l0 14"/><path d="M5 12l14 0"/></Icon>;
const IconUserX  = (c) => <Icon color={c} size={14}><path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"/><path d="M3 21v-2a4 4 0 0 1 4 -4h7"/><path d="M22 22l-5 -5"/><path d="M17 22l5 -5"/></Icon>;
const IconBack   = (c) => <Icon color={c}><path d="M5 12l14 0"/><path d="M5 12l6 6"/><path d="M5 12l6 -6"/></Icon>;

// ── Helpers ────────────────────────────────────────────────────
const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};
const fmtDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(); yest.setDate(yest.getDate() - 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, today))   return "Aujourd'hui";
  if (sameDay(d, yest))    return 'Hier';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
};
const authFetch = (url, opts = {}) => {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};
const initials = (u) => ((u?.prenom?.[0] || '') + (u?.nom?.[0] || u?.email?.[0] || '?')).toUpperCase();
const isArcusRole = (r) => ['manager', 'souscripteur', 'accounts', 'it'].includes(r);
const roleStyle = (r) => {
  if (r === 'client')   return { bg: '#DBEAFE', color: '#1D4ED8', label: 'Client' };
  if (r === 'courtier') return { bg: '#FEF3C7', color: '#92400E', label: 'Courtier' };
  if (r === 'voyageur') return { bg: '#E0E7FF', color: '#3730A3', label: 'Voyageur' };
  if (isArcusRole(r))   return { bg: '#FFEDD5', color: '#9A3412', label: 'Arcus' };
  return { bg: '#F1F5F9', color: '#475569', label: r };
};

export default function ChatPanel() {
  const { user, isArcus, isClient, isCourtier, isVoyageur } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();

  // ── Phase D6 : Le chat voyageur n'est accessible que dans sa fiche sinistre ──
  // Sur toute autre page, le chat (bulle + panneau) disparaît pour le voyageur.
  // Les profils Arcus / client / courtier gardent leur comportement habituel.
  const onDossierPage = /^\/sinistres\/[^/]+\/dossiers\/[^/]+/.test(location.pathname);
  const chatAllowed = isVoyageur ? onDossierPage : true;

  const [open, setOpen]               = useState(true);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [adminMode, setAdminMode]     = useState(false);     // true if user is Arcus
  const [convList, setConvList]       = useState([]);        // admin only: list of conversations
  const [currentConv, setCurrentConv] = useState(null);      // currently displayed conv (full object)
  const [messages, setMessages]       = useState([]);
  const [draft, setDraft]             = useState('');
  const [sending, setSending]         = useState(false);
  const [unread, setUnread]           = useState(0);
  const [pickerOpen, setPickerOpen]   = useState(false);     // admin participant picker
  const [allAdmins, setAllAdmins]     = useState([]);

  const messagesEndRef = useRef(null);
  const lastFetchedAtRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // ── Initial load ───────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await authFetch('/api/chat/me').then(r => r.json());
        if (!alive) return;
        if (r.admin) {
          setAdminMode(true);
          setConvList(r.conversations || []);
        } else {
          setAdminMode(false);
          if (r.conversation) {
            setCurrentConv(r.conversation);
            await loadMessages(r.conversation.id, true);
          }
        }
      } catch (e) {
        setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Contextual switching for Arcus admins ──────────────────
  useEffect(() => {
    if (!adminMode) return;
    const path = location.pathname;
    let url = null;
    // /clients/:id/detail or /clients/:id
    const mClient = path.match(/^\/clients\/([^/]+)(\/detail)?$/);
    if (mClient) url = `/api/chat/by-client/${mClient[1]}`;
    // /sinistres/:procId/dossiers/:dossierId
    const mDossier = path.match(/^\/sinistres\/[^/]+\/dossiers\/([^/]+)/);
    if (mDossier) url = `/api/chat/by-dossier/${mDossier[1]}`;
    // /intermediaires/:id
    const mInter = path.match(/^\/intermediaires\/([^/]+)/);
    if (mInter) url = `/api/chat/by-intermediaire/${mInter[1]}`;

    if (!url) {
      // Phase D6 fix : on n'est sur aucune page contextuelle (ex: retour à la liste des dossiers)
      // → on quitte la conversation courante (retour à "NO CONVERSATION")
      setCurrentConv(null);
      setMessages([]);
      return;
    }
    (async () => {
      try {
        const r = await authFetch(url).then(r => r.json());
        if (r.conversation) {
          setCurrentConv(r.conversation);
          await loadMessages(r.conversation.id, true);
          await refreshConvList();
        } else if (r.error) {
          // No external user account for this entity yet — silently ignore
          setCurrentConv(null);
          setMessages([]);
        }
      } catch (e) {
        // Silent
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, adminMode]);

  // ── Phase D6 : fermer le chat quand le voyageur quitte sa fiche sinistre ──
  useEffect(() => {
    if (isVoyageur && !onDossierPage) {
      setOpen(false);
    }
  }, [isVoyageur, onDossierPage]);

  // ── Polling messages while panel open ──────────────────────
  useEffect(() => {
    if (!currentConv || !open) return;
    pollIntervalRef.current = setInterval(() => {
      loadMessages(currentConv.id, false);
    }, POLL_MS);
    return () => clearInterval(pollIntervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConv?.id, open]);

  // ── Polling unread when minimized ──────────────────────────
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const r = await authFetch('/api/chat/unread-count').then(r => r.json());
        setUnread(r.total || 0);
      } catch {}
    };
    fetchUnread();
    const id = setInterval(fetchUnread, UNREAD_POLL_MS);
    return () => clearInterval(id);
  }, []);

  // ── Auto-scroll to bottom on new messages ──────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // ── Mark as read on open + when new messages arrive ────────
  useEffect(() => {
    if (!currentConv || !open) return;
    authFetch(`/api/chat/conversations/${currentConv.id}/read`, { method: 'PUT' })
      .then(() => setUnread(u => Math.max(0, u - (messages.filter(m => m.sender_id !== user?.id).length))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConv?.id, open]);

  // ── Helpers ────────────────────────────────────────────────
  async function loadMessages(convId, full) {
    try {
      const since = !full && lastFetchedAtRef.current ? `?since=${encodeURIComponent(lastFetchedAtRef.current)}` : '';
      const newMsgs = await authFetch(`/api/chat/conversations/${convId}/messages${since}`).then(r => r.json());
      if (!Array.isArray(newMsgs)) return;
      if (full) {
        setMessages(newMsgs);
      } else if (newMsgs.length) {
        setMessages(prev => {
          const ids = new Set(prev.map(m => m.id));
          return [...prev, ...newMsgs.filter(m => !ids.has(m.id))];
        });
      }
      if (newMsgs.length) lastFetchedAtRef.current = newMsgs[newMsgs.length - 1].created_at;
      else if (full) lastFetchedAtRef.current = new Date().toISOString();
    } catch (e) { /* silent */ }
  }

  async function refreshConvList() {
    if (!adminMode) return;
    try {
      const r = await authFetch('/api/chat/me').then(r => r.json());
      if (r.conversations) setConvList(r.conversations);
    } catch {}
  }

  async function sendMessage() {
    if (!draft.trim() || !currentConv || sending) return;
    setSending(true);
    setError('');
    try {
      const msg = await authFetch(`/api/chat/conversations/${currentConv.id}/messages`, {
        method: 'POST',
        body: JSON.stringify({ body: draft.trim() }),
      }).then(r => r.json());
      if (msg.error) throw new Error(msg.error);
      setMessages(prev => [...prev, msg]);
      setDraft('');
      lastFetchedAtRef.current = msg.created_at;
      refreshConvList();
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  }

  async function openConv(convId) {
    try {
      // Use participants API to get full info; the /me endpoint already gave us metadata,
      // so just load messages
      const conv = convList.find(c => c.id === convId);
      if (conv) setCurrentConv({ id: convId, ...conv });
      await loadMessages(convId, true);
    } catch (e) { setError(e.message); }
  }

  async function loadAdmins() {
    try {
      const r = await authFetch('/api/chat/arcus-admins').then(r => r.json());
      setAllAdmins(r || []);
    } catch {}
  }

  async function addParticipant(userId) {
    if (!currentConv) return;
    try {
      const r = await authFetch(`/api/chat/conversations/${currentConv.id}/participants`, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId }),
      }).then(r => r.json());
      if (r.error) throw new Error(r.error);
      // Reload conversation to refresh participants
      if (adminMode) {
        // No direct endpoint; re-derive by hitting the matching by-* if possible — simplest: reload via location effect
        // Quick fix: refetch contextual url
        const path = location.pathname;
        const mClient = path.match(/^\/clients\/([^/]+)/);
        if (mClient) {
          const conv = await authFetch(`/api/chat/by-client/${mClient[1]}`).then(r => r.json());
          if (conv.conversation) setCurrentConv(conv.conversation);
        }
      }
      setPickerOpen(false);
    } catch (e) { setError(e.message); }
  }

  async function removeParticipant(userId) {
    if (!currentConv) return;
    if (!window.confirm(t('chat.confirm_remove_participant'))) return;
    try {
      await authFetch(`/api/chat/conversations/${currentConv.id}/participants/${userId}`, { method: 'DELETE' });
      // Refresh
      const path = location.pathname;
      const mClient = path.match(/^\/clients\/([^/]+)/);
      if (mClient) {
        const conv = await authFetch(`/api/chat/by-client/${mClient[1]}`).then(r => r.json());
        if (conv.conversation) setCurrentConv(conv.conversation);
      }
    } catch (e) { setError(e.message); }
  }

  // ── Phase D6 : ne rien afficher si le chat n'est pas autorisé (voyageur hors de sa fiche) ──
  if (!chatAllowed) return null;

  // ── Render minimized bubble ────────────────────────────────
  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        style={{
          position: 'fixed', right: 22, bottom: 22, width: 56, height: 56,
          borderRadius: '50%', background: ORANGE, color: '#fff',
          border: 'none', cursor: 'pointer', zIndex: 9999,
          boxShadow: '0 6px 18px rgba(0,0,0,.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        title={t('chat.open')}>
        {IconChat('#fff', 24)}
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4, minWidth: 22, height: 22,
            padding: '0 6px', borderRadius: 11, background: '#DC2626', color: '#fff',
            fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid #fff',
          }}>{unread > 99 ? '99+' : unread}</span>
        )}
      </button>
    );
  }

  // ── Render full panel ──────────────────────────────────────
  const meName = ((user?.prenom || '') + ' ' + (user?.nom || '')).trim() || user?.email || '';
  const externalName = currentConv ? `${currentConv.external_prenom || ''} ${currentConv.external_nom || ''}`.trim() || currentConv.external_email : '';
  const activeParticipants = (currentConv?.participants || []).filter(p => !p.left_at);
  const currentIsParticipant = !!activeParticipants.find(p => p.user_id === user?.id);

  return (
    <div style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, width: 360,
      background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(6px)',
      borderLeft: '1px solid #E2E8F0', zIndex: 9998,
      boxShadow: '-4px 0 24px rgba(0,0,0,.08)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 16px', background: DARK, color: '#fff',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        {IconChat('#fff', 18)}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {currentConv ? (adminMode ? externalName : t('chat.with_arcus')) : t('chat.no_conv')}
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>
            {currentConv ? `${activeParticipants.length} ${t('chat.participants')}` : (adminMode ? t('chat.select_conv') : '')}
          </div>
        </div>
        <button onClick={() => setOpen(false)}
          style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', padding: 4 }}
          title={t('chat.minimize')}>
          {IconMin('#9CA3AF')}
        </button>
      </div>

      {/* Participants strip (compact) */}
      {currentConv && (
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9', background: '#FAFAFA',
          display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {activeParticipants.map(p => {
            const isMe = p.user_id === user?.id;
            const isOwner = p.user_id === currentConv.external_user_id;
            const rs = roleStyle(p.role);
            const name = `${p.prenom || ''} ${p.nom || ''}`.trim() || p.email;
            return (
              <span key={p.user_id} title={name}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 7px 3px 4px', borderRadius: 99,
                  background: rs.bg, color: rs.color, fontSize: 11, fontWeight: 600 }}>
                <span style={{ width: 18, height: 18, borderRadius: '50%', background: rs.color, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 }}>
                  {initials(p)}
                </span>
                {isMe ? t('chat.you') : (p.prenom || p.nom || p.email?.split('@')[0])}
                {isArcus && !isOwner && p.user_id !== user?.id && (
                  <button onClick={() => removeParticipant(p.user_id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginLeft: 2, color: rs.color, opacity: .6 }}
                    title={t('chat.remove_participant')}>
                    {IconUserX(rs.color)}
                  </button>
                )}
              </span>
            );
          })}
          {isArcus && currentIsParticipant && (
            <button onClick={() => { loadAdmins(); setPickerOpen(true); }}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 99,
                background: '#fff', border: '1px dashed #CBD5E1', color: '#64748B', fontSize: 11, cursor: 'pointer', fontWeight: 500 }}>
              {IconPlus('#64748B')} {t('chat.add')}
            </button>
          )}
        </div>
      )}

      {/* Admin: list of conversations when no current conv */}
      {adminMode && !currentConv && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {convList.length === 0
            ? <div style={{ padding: 24, fontSize: 13, color: '#94A3B8', textAlign: 'center' }}>{t('chat.no_conv_yet')}</div>
            : convList.map(c => (
              <button key={c.id} onClick={() => openConv(c.id)}
                style={{ width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer',
                  borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: ORANGE, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                  {((c.external_prenom?.[0] || '') + (c.external_nom?.[0] || '')).toUpperCase() || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1E293B',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.client_nom || `${c.external_prenom || ''} ${c.external_nom || ''}`.trim() || c.external_email}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748B',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.last_message_body || t('chat.no_messages_yet')}
                  </div>
                </div>
                {c.unread_count > 0 && (
                  <span style={{ minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, background: '#DC2626', color: '#fff',
                    fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {c.unread_count}
                  </span>
                )}
              </button>
            ))
          }
        </div>
      )}

      {/* Messages thread */}
      {currentConv && (
        <>
          {adminMode && (
            <button onClick={() => setCurrentConv(null)}
              style={{ background: '#F8FAFC', border: 'none', borderBottom: '1px solid #F1F5F9',
                padding: '6px 14px', textAlign: 'left', cursor: 'pointer', fontSize: 11, color: '#64748B',
                display: 'flex', alignItems: 'center', gap: 6 }}>
              {IconBack('#64748B')} {t('chat.back_list')}
            </button>
          )}
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 14px 6px', background: '#FAFAFA' }}>
            {messages.length === 0
              ? <div style={{ padding: 20, fontSize: 12, color: '#94A3B8', textAlign: 'center' }}>{t('chat.send_first_message')}</div>
              : messages.map((m, i) => {
                const prev = messages[i - 1];
                const isMe = m.sender_id === user?.id;
                const sameAuthor = prev && prev.sender_id === m.sender_id;
                const sameDayAsPrev = prev && new Date(m.created_at).toDateString() === new Date(prev.created_at).toDateString();
                const senderName = `${m.sender_prenom || ''} ${m.sender_nom || ''}`.trim();
                return (
                  <React.Fragment key={m.id}>
                    {(!sameDayAsPrev) && (
                      <div style={{ textAlign: 'center', fontSize: 10, color: '#94A3B8', margin: '12px 0 8px',
                        textTransform: 'uppercase', letterSpacing: '.06em' }}>{fmtDay(m.created_at)}</div>
                    )}
                    <div style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginTop: sameAuthor ? 3 : 8 }}>
                      <div style={{ maxWidth: '78%' }}>
                        {!isMe && !sameAuthor && (
                          <div style={{ fontSize: 10, color: '#64748B', marginBottom: 3, paddingLeft: 4, fontWeight: 600 }}>
                            {senderName || m.sender_role}
                          </div>
                        )}
                        <div style={{
                          padding: '7px 11px', borderRadius: 12,
                          background: isMe ? ORANGE : '#fff',
                          color: isMe ? '#fff' : '#1E293B',
                          border: isMe ? 'none' : '1px solid #E2E8F0',
                          fontSize: 13, lineHeight: 1.4,
                          wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                        }}>
                          {m.body}
                        </div>
                        <div style={{ fontSize: 9, color: '#94A3B8', marginTop: 2,
                          textAlign: isMe ? 'right' : 'left', paddingRight: isMe ? 4 : 0, paddingLeft: isMe ? 0 : 4 }}>
                          {fmtTime(m.created_at)}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })
            }
            <div ref={messagesEndRef}/>
          </div>

          {/* Input */}
          <div style={{ padding: '10px 12px', borderTop: '1px solid #E2E8F0', background: '#fff',
            display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder={t('chat.message_placeholder')}
              rows={1}
              disabled={sending}
              style={{
                flex: 1, resize: 'none', minHeight: 38, maxHeight: 100,
                padding: '9px 11px', borderRadius: 8, border: '1px solid #CBD5E1',
                fontSize: 13, fontFamily: 'inherit', outline: 'none',
              }}
              onFocus={e => e.target.style.borderColor = ORANGE}
              onBlur={e => e.target.style.borderColor = '#CBD5E1'}
            />
            <button onClick={sendMessage} disabled={!draft.trim() || sending}
              style={{
                width: 38, height: 38, borderRadius: 8, background: ORANGE,
                color: '#fff', border: 'none', cursor: draft.trim() && !sending ? 'pointer' : 'not-allowed',
                opacity: draft.trim() && !sending ? 1 : .5,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
              {IconSend('#fff')}
            </button>
          </div>
          {error && <div style={{ padding: '6px 12px', fontSize: 11, color: '#DC2626', background: '#FEF2F2' }}>{error}</div>}
        </>
      )}

      {/* External: no conv state */}
      {!adminMode && !currentConv && !loading && (
        <div style={{ flex: 1, padding: 24, fontSize: 13, color: '#94A3B8', textAlign: 'center' }}>
          {t('chat.loading_error')}
        </div>
      )}

      {/* Participant picker (admin) */}
      {pickerOpen && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,.95)', padding: 16, zIndex: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{t('chat.add_admin')}</div>
            <button onClick={() => setPickerOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}>{IconClose('#64748B')}</button>
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
            {allAdmins.filter(a => !activeParticipants.find(p => p.user_id === a.id)).map(a => (
              <button key={a.id} onClick={() => addParticipant(a.id)}
                style={{ width: '100%', padding: '8px 10px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 6,
                  marginBottom: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left' }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#FFEDD5', color: '#9A3412',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>
                  {initials(a)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{`${a.prenom || ''} ${a.nom || ''}`.trim() || a.email}</div>
                  <div style={{ fontSize: 10, color: '#64748B' }}>{roleStyle(a.role).label} · {a.email}</div>
                </div>
              </button>
            ))}
            {allAdmins.filter(a => !activeParticipants.find(p => p.user_id === a.id)).length === 0 && (
              <div style={{ textAlign: 'center', padding: 16, fontSize: 12, color: '#94A3B8' }}>
                {t('chat.all_admins_already')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
